/* eslint-disable react/jsx-no-constructed-context-values */
import { useSettings } from '@backpackjs/storefront';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { GlobalProductPageContext } from '../../contexts/globalProductPageContext';
import { useDataLayerActions, useSubscriptionWidget } from '../../hooks';
import { AddToCart, Image } from '../../snippets';
import { OkendoStarRating } from '../../snippets/Okendo/OkendoStarRating';
import { Breadcrumbs } from '../../snippets/PDP/Breadcrumbs';
import { ProductDetails } from './ProductDetails';
import { ProductHeader } from './ProductHeader';
import { ProductMedia } from './ProductMedia';
import { ProductMediaAlt } from './ProductMediaAlt';
import { useSelectedVariant } from './useSelectedVariant';
import { ProductMediaGallery } from './ProductMediaGallery';
import { useGlobalContext } from '../../contexts';

export function ProductTemplate({ renderSections, product }) {
  const settings = useSettings();
  const sections = renderSections();
  const { sendViewProductEvent } = useDataLayerActions();
  const {
    state: { testVariant },
  } = useGlobalContext();
  const {
    selectedVariant,
    setSelectedOptions,
    selectedOptions,
    selectedMedia,
  } = useSelectedVariant(product);

  const { productSubscriptionWidget } = useSubscriptionWidget(product);

  const [productTestResults, setProductTestResults] = useState(null);
  const [recommendedProducts, setRecommendedProducts] = useState(null);
  const [recommendedFilterFrequency, setRecommendedFilterFrequency] =
    useState(null);
  const [selectedSellingPlan, setSelectedSellingPlan] = useState(null);
  const [productByline, setProductByline] = useState(null);
  const [purchaseType, setPurchaseType] = useState(
    product.sellingPlanGroupCount > 0 ? 'subscription' : 'one-time'
  );

  const { enabledStarRating } = { ...settings?.product?.reviews };

  // pass the scroll reference to the product details section
  const { ref: scrollReference, entry } = useInView({
    threshold: 0,
    initialInView: true,
  });

  // separate the product detail accordions from the below the fold sections
  const unrenderSections = [
    'product-detail-accordions',
    'product-additional-details',
  ];
  const {
    productAdditionalDetails,
    productDetailAccordionsSection,
    belowTheFoldSections,
  } = useMemo(() => {
    if (!sections) return {};
    return {
      productAdditionalDetails: sections.find(({ props }) => {
        return props?.['data-comp'] === 'product-additional-details';
      }),
      productDetailAccordionsSection: sections.find(({ props }) => {
        return (
          props?.['data-comp'] === 'product-detail-accordions' && !props?.hidden
        );
      }),
      belowTheFoldSections: sections.filter(({ props }) => {
        return !unrenderSections.includes(props?.['data-comp']);
      }),
    };
  }, [sections]);

  useEffect(() => {
    sendViewProductEvent({ product, selectedVariant });
  }, [selectedVariant?.product?.handle, sendViewProductEvent]);

  // set class for sticky header
  const stickyPromobar =
    settings?.header?.promobar?.enabled &&
    !settings?.header?.promobar?.autohide;
  const stickyTopClass = stickyPromobar
    ? 'md:top-[calc(var(--header-height)+var(--promobar-height)+2.5rem)] xl:top-[calc(var(--header-height)+var(--promobar-height)+3rem)]'
    : 'md:top-[calc(var(--header-height)+2.5rem)] xl:top-[calc(var(--header-height)+3rem)]';

  // set global product data
  const globalProductData = {
    selectedSellingPlan,
    setSelectedSellingPlan,
    purchaseType,
    setPurchaseType,
    recommendedFilterFrequency,
    setRecommendedFilterFrequency,
    selectedVariant,
    recommendedProducts,
    setRecommendedProducts,
    scrollReference,
    productSubscriptionWidget,
    productTestResults,
    setProductTestResults,
    productByline,
    setProductByline,
    productAdditionalDetails,
  };

  return (
    <GlobalProductPageContext.Provider value={globalProductData}>
      <div
        className={`product__sticky-cta fixed left-0 top-0 z-20 w-full bg-white py-[16px] md:py-[5px] ${
          !entry || entry?.boundingClientRect?.top > 0 ? 'hidden' : 'block'
        }`}
      >
        <div className="px-contained container mx-auto max-w-[var(--content-max-width)] px-4">
          <div className="items-center justify-between sm:flex">
            <div className="hidden items-center sm:flex">
              {
                /* Image */
                (selectedVariant?.image?.src ||
                  product?.featuredImage?.src) && (
                  <div className="relative aspect-square w-[60px] flex-[0_0_60px] bg-background max-sm:hidden">
                    {product?.featuredImage?.src &&
                      !selectedVariant?.image?.src && (
                        <Image
                          alt={product?.title}
                          className="absolute h-full w-full object-contain !mix-blend-multiply"
                          width="60px"
                          height="60px"
                          src={product?.featuredImage?.src}
                        />
                      )}

                    {selectedVariant?.image?.src && (
                      <Image
                        alt={selectedVariant?.title}
                        className="absolute h-full w-full object-contain !mix-blend-multiply"
                        width="60px"
                        height="60px"
                        src={selectedVariant?.image?.src}
                      />
                    )}
                  </div>
                )
              }

              <div className="stick-product-details mr-[20px] sm:ml-[40px]">
                {product?.title && (
                  <h4 className="hidden text-[16px] sm:block">
                    {selectedVariant?.displayName.split('- Default Title')[0]}
                  </h4>
                )}

                {enabledStarRating && (
                  <OkendoStarRating productId={product?.legacyResourceId} />
                )}
              </div>
            </div>

            <AddToCart
              className="!mt-0 !px-[32px] !py-[10px] max-sm:mx-auto max-sm:block max-sm:max-w-[262px] max-sm:text-[14px]"
              addToCartText="Add to Cart"
              selectedVariant={selectedVariant}
              purchaseType={purchaseType}
              selectedSellingPlan={selectedSellingPlan}
            />
          </div>
        </div>
      </div>

      <div className="product__top-section md:px-contained mt-3 max-w-[var(--content-max-width)] md:mx-auto md:mt-7 md:pb-12">
        <Breadcrumbs product={product} />
        <div className="mx-auto flex flex-col md:flex-row">
          <div className="block px-4 pb-4 md:hidden">
            <ProductHeader
              selectedSellingPlan={selectedSellingPlan}
              purchaseType={purchaseType}
              product={product}
              selectedVariant={selectedVariant}
            />
          </div>
          <div className="product__media-section order-0 my-0 md:flex-[0_0_50%] xl:flex-[0_0_55%]">
            <div className={`md:sticky ${stickyTopClass}`}>
              {[
                'clean-water-pitcher',
                'clean-water-pitcher-filter-3-pack',
                'clean-water-pitcher-filter-6-pack',
              ].includes(product.handle) ? (
                (testVariant?.['clear-220']?.toLowerCase() ?? '') === 'variant1' ?
                (
                  <ProductMediaGallery
                    selectedMedia={selectedMedia}
                    selectedVariant={selectedVariant}
                    product={product}
                  />
                ) : (
                  <ProductMediaAlt
                    selectedMedia={selectedMedia}
                    selectedVariant={selectedVariant}
                    product={product}
                  />
                )
              ) : (
                <ProductMedia
                  selectedMedia={selectedMedia}
                  selectedVariant={selectedVariant}
                  product={product}
                />
              )}
            </div>
          </div>

          <div className="product__details-section order-1 px-4 md:flex-[0_0_50%] xl:flex-[0_0_45%]">
            <div
              className={`flex flex-col gap-y-4 md:sticky ${stickyTopClass}`}
            >
              <div className="hidden md:block">
                <ProductHeader
                  selectedSellingPlan={selectedSellingPlan}
                  purchaseType={purchaseType}
                  product={product}
                  selectedVariant={selectedVariant}
                />
              </div>
              <ProductDetails
                scrollReference={scrollReference}
                product={product}
                productDetailAccordionsSection={productDetailAccordionsSection}
                selectedOptions={selectedOptions}
                selectedVariant={selectedVariant}
                setSelectedOptions={setSelectedOptions}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="product__page-sections">
        {belowTheFoldSections || null}
      </div>
    </GlobalProductPageContext.Provider>
  );
}

ProductTemplate.displayName = 'ProductTemplate';
